/**
 * useQuery-backed hook returning the compiled section/folder tree for a
 * publication (`IStoriesFolder[]`) plus the root folder id.
 *
 * Thin wrapper over `usePublicationFoldersHierarchy` — does NOT re-fetch. It
 * applies the same `compilePublicationFolderNodes → alphabetizeNodes →
 * sortItemsByOrder` transform the retired `selectPublicationFolderStructure`
 * selector applied in the compose-app era.
 *
 * Other features import this from `PublicationView/hooks/` (same cross-feature
 * pattern as `useReportsForEntity` from Add Tile) — do NOT move to a shared
 * hooks folder.
 *
 * Auth (§1) + entityId>0 guard (§9) + error toast are inherited from the
 * underlying `usePublicationFoldersHierarchy` (pass `null` to disable).
 */

import { useMemo } from 'react';

import { IStoriesFolder } from '../../../../../types';
import { alphabetizeNodes, sortItemsByOrder } from '../../../../utils';
import { compilePublicationFolderNodes } from './compilePublicationFolderNodes';
import {
  usePublicationFoldersHierarchy,
  UsePublicationFoldersHierarchyParams
} from './usePublicationFoldersHierarchy';

export type UsePublicationFolderStructureParams = UsePublicationFoldersHierarchyParams;

export function usePublicationFolderStructure(
  params: UsePublicationFolderStructureParams,
  errorMessage: string
) {
  const query = usePublicationFoldersHierarchy(params, errorMessage);

  const publicationFolders = query.data?.publicationFolders ?? [];
  const publicationRootFolderId = query.data?.publicationRootFolderId ?? '';

  const publicationFolderStructure: IStoriesFolder[] = useMemo(
    () => sortItemsByOrder(alphabetizeNodes(compilePublicationFolderNodes(publicationFolders) || [])),
    [publicationFolders]
  );

  return {
    ...query,
    publicationFolderStructure,
    publicationRootFolderId
  };
}
